import { useOutletContext } from 'react-router-dom';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';

import { AdminSection, OutletContext } from '@typings';
import { userSelector } from '@selectors';
import { useSelector } from '@hooks';
import { getFormattedMemory, getGpuFromNodePool } from '@utils';

import { Button, Icon, Modal, Render, Table } from '@components';
import { ClusterEditNodePoolModal } from '@components/Cluster';
import { GpuModels } from '@components/Ui';

type HeaderCell = {
  text: string;
  className?: string;
};

const HEADERS: HeaderCell[] = [
  { text: 'Name', className: 'pl-10' },
  { text: 'Size' },
  { text: 'CPU cores / Available' },
  { text: 'CPU Watts' },
  { text: 'Memory / Available' },
  { text: 'Disk size' },
  { text: 'GPU devices' },
  { text: '', className: 'pr-10' },
];

export const AdminSectionClusterNodesPage = () => {
  const { cluster, setCluster } =
    useOutletContext<OutletContext.AdminSectionCluster>();

  const { systemRole } = useSelector(userSelector);

  const handleUpdateNodePool = (
    nodePoolParams: Pick<
      AdminSection.Cluster['orchestrator']['resourcePoolTypes'][number],
      'name' | 'idleSize' | 'cpuMaxWatts' | 'cpuMinWatts'
    >,
  ) => {
    setCluster((prev) => {
      const nodePool = prev.orchestrator.resourcePoolTypes.find(
        (node) => node.name === nodePoolParams.name,
      );
      if (nodePool) {
        Object.assign(nodePool, nodePoolParams);
      }
    });
  };

  return (
    <>
      <Table className="w-full auto-rows-min grid-cols-[repeat(7,minmax(max-content,auto))_min-content] overflow-auto rounded-none border-b border-stroke">
        <Table.Header>
          {HEADERS.map(({ text, className }) => (
            <Table.Head key={text} className={`bg-background ${className}`}>
              {text}
            </Table.Head>
          ))}
        </Table.Header>

        <Table.Body>
          {cluster.orchestrator.resourcePoolTypes.map((node) => {
            const {
              name,
              minSize = 0,
              idleSize = 0,
              maxSize = 0,
              diskSize,
              cpuMinWatts,
              cpuMaxWatts,
              memory,
              availableMemory,
              cpu,
              availableCpu,
            } = node;

            const gpuModels = getGpuFromNodePool(node, {
              withPlaceholder: true,
            });

            /**
             * Created key for re-render row entry on node pool update
             */
            const key = `${name}-${idleSize}-${cpuMinWatts}-${cpuMaxWatts}`;

            return (
              <Table.Row key={key}>
                <Table.Cell className="pl-10">{name}</Table.Cell>

                <Table.Cell>
                  <div className="flex gap-10">
                    <div>
                      <p className="text-caption text-neural-03">Min</p>
                      <p>{minSize}</p>
                    </div>

                    <div>
                      <p className="text-caption text-neural-03">idle</p>
                      <p>{idleSize}</p>
                    </div>

                    <div>
                      <p className="text-caption text-neural-03">Max</p>
                      <p>{maxSize}</p>
                    </div>
                  </div>
                </Table.Cell>

                <Table.Cell>
                  {cpu} / {availableCpu}
                </Table.Cell>

                <Table.Cell>
                  {cpuMinWatts || ''} - {cpuMaxWatts || ''}
                </Table.Cell>

                <Table.Cell>
                  {getFormattedMemory(memory)} /{' '}
                  {getFormattedMemory(availableMemory)}
                </Table.Cell>

                <Table.Cell>{getFormattedMemory(diskSize)}</Table.Cell>

                <Table.Cell>
                  <GpuModels models={gpuModels} />
                </Table.Cell>

                <Table.Cell className="flex-row items-center pr-10">
                  {systemRole === AdminSection.Role.Admin && (
                    <Modal
                      content={
                        <ClusterEditNodePoolModal
                          nodePool={node}
                          onNodePoolUpdate={handleUpdateNodePool}
                        />
                      }
                    >
                      <Button
                        variant="ghost"
                        className="h-6 w-6 p-0 text-[18px]"
                      >
                        <Icon icon={faPenToSquare} />
                      </Button>
                    </Modal>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <Render if={cluster.orchestrator.resourcePoolTypes.length === 0}>
        <p className="break-words py-20 text-center text-neural-04">
          No nodes found
        </p>
      </Render>
    </>
  );
};
